import { deriveDoorGeometry, validateDoorConfig } from './door-model';
import { buildReferenceTcnText, REFERENCE_CONFIG } from './configurator-reference';
import {
  buildTcnMachiningPlan,
  inspectTcnToolEnvelope,
  TCN_CAM_TEMPLATE,
} from './configurator-tcn-toolpath';
import type {
  TcnMachiningPlan,
  TcnToolpathPass,
  TcnToolpathPoint,
} from './configurator-tcn-toolpath';
import type { ConfiguratorState, ValidationIssue } from './configurator-types';

export type TcnReleaseState =
  | 'reference-match-review-required'
  | 'parametric-program-review-required'
  | 'invalid-configuration';

export type TcnArtifactKind =
  | 'reference-review-program'
  | 'parametric-machining-program'
  | 'invalid-configuration-stub';

export type TcnCompatibility = {
  releaseState: TcnReleaseState;
  artifactKind: TcnArtifactKind;
  containsMachining: boolean;
  fingerprint: string;
  issues: ValidationIssue[];
  warnings: string[];
};

export type TcnArtifact = TcnCompatibility & {
  text: string;
  bytes: Uint8Array;
  filename: string;
};

const RACCORD_COLLINEAR_TOLERANCE_MM = 0.001;

/**
 * Every valid configuration now receives a real TCN profile. The supplied source
 * remains a special golden case and is returned byte-for-byte. Generated programs
 * are deliberately review-only until tool 1009, the external macro and the datum
 * assumption have been verified on the target machine.
 */
export function analyzeTcnCompatibility(config: ConfiguratorState): TcnCompatibility {
  const issues = collectValidationIssues(config);
  const fingerprint = buildTcnFingerprint(config);
  const hasErrors = issues.some(issue => issue.level === 'error');
  const isReferenceMatch = !hasErrors && matchesExactReference(config);

  const releaseState: TcnReleaseState = hasErrors
    ? 'invalid-configuration'
    : isReferenceMatch
      ? 'reference-match-review-required'
      : 'parametric-program-review-required';
  const artifactKind: TcnArtifactKind = hasErrors
    ? 'invalid-configuration-stub'
    : isReferenceMatch
      ? 'reference-review-program'
      : 'parametric-machining-program';

  return {
    releaseState,
    artifactKind,
    containsMachining: !hasErrors,
    fingerprint,
    issues,
    warnings: buildWarnings(config, releaseState),
  };
}

export function buildTcnArtifact(config: ConfiguratorState): TcnArtifact {
  const compatibility = analyzeTcnCompatibility(config);
  const geometry = deriveDoorGeometry(config);
  const marker =
    compatibility.releaseState === 'reference-match-review-required'
      ? 'SOURCE-REVIEW'
      : compatibility.releaseState === 'parametric-program-review-required'
        ? 'PARAMETRIC-REVIEW-ONLY'
        : 'INVALID';
  const filename =
    [
      'bdv',
      config.productId,
      `${formatFileNumber(config.widthMm)}x${formatFileNumber(config.heightMm)}x${formatFileNumber(config.thicknessMm)}`,
      geometry.variant,
      marker,
      compatibility.fingerprint,
    ].join('-') + '.tcn';

  if (compatibility.releaseState === 'reference-match-review-required') {
    const text = buildReferenceTcnText();
    return {
      ...compatibility,
      text,
      bytes: encodeUtf16LeWithBom(text),
      filename,
    };
  }

  const text =
    compatibility.releaseState === 'invalid-configuration'
      ? buildInvalidProgram(config)
      : buildParametricProgram(config);

  return {
    ...compatibility,
    text,
    bytes: encodeUtf16LeWithBom(text),
    filename,
  };
}

function buildParametricProgram(config: ConfiguratorState) {
  const plan = buildTcnMachiningPlan(config);
  const operationLines = plan.passes.flatMap(pass => {
    const [setup, ...segments] = removeCollinearRaccordPoints(pass.points);
    if (!setup) return [];
    return [
      buildMillSetup(pass.operationSequence, setup.xMm, setup.yMm, pass.depthMm),
      ...buildRaccordOperations(plan, pass, segments),
    ];
  });

  const lines = [
    'TPA\\ALBATROS\\EDICAD\\02.00:1564:r0w0h0s51',
    '::SIDE=0;1;',
    `::UNm DL=${formatTcnDimension(plan.stock.widthMm)} DH=${formatTcnDimension(plan.stock.heightMm)} DS=${formatTcnDimension(plan.stock.thicknessMm)}`,
    "'tcn version=2.8.21",
    "'code=unicode",
    ...baseSections(),
    'SIDE#0{',
    `W#1511{ ::WT2 WF=1  #8098=${plan.macro} }W`,
    '}SIDE',
    'SIDE#1{',
    ...operationLines,
    '}SIDE',
    ...emptySideSections(),
  ];

  return `${lines.join('\r\n')}\r\n`;
}

function buildInvalidProgram(config: ConfiguratorState) {
  const geometry = deriveDoorGeometry(config);
  const lines = [
    'TPA\\ALBATROS\\EDICAD\\02.00:1564:r0w0h0s51',
    '::SIDE=0;1;',
    `::UNm DL=${formatTcnDimension(geometry.stock.widthMm)} DH=${formatTcnDimension(geometry.stock.heightMm)} DS=${formatTcnDimension(geometry.stock.thicknessMm)}`,
    "'tcn version=2.8.21",
    "'code=unicode",
    ...baseSections(),
    'SIDE#1{',
    '$=INVALID CONFIGURATION - machining omitted',
    '}SIDE',
    ...emptySideSections(),
  ];

  return `${lines.join('\r\n')}\r\n`;
}

function baseSections() {
  return [
    'EXE{',
    '#0=0',
    '#1=0',
    '#2=0',
    '#3=0',
    '#4=0',
    '}EXE',
    'OFFS{',
    '#0=0.0|0',
    '#1=0.0|0',
    '#2=0.0|0',
    '}OFFS',
    'VARV{',
    '#0=0.0|0',
    '#1=0.0|0',
    '}VARV',
    'VAR{',
    '}VAR',
    'SPEC{',
    '}SPEC',
    'INFO{',
    '}INFO',
    'OPTI{',
    ':: OPTKIND=%;0 OPTROUTER=%;0 LSTCOD=%0%1%2',
    '}OPTI',
    'LINK{',
    '}LINK',
  ];
}

function emptySideSections() {
  return [
    'SIDE#3{',
    '::DX=0 XY=1',
    '}SIDE',
    'SIDE#4{',
    '::DX=0 XY=1',
    '}SIDE',
    'SIDE#5{',
    '::DX=0 XY=1',
    '}SIDE',
    'SIDE#6{',
    '::DX=0 XY=1',
    '}SIDE',
  ];
}

function buildMillSetup(sequence: 34 | 36, xMm: number, yMm: number, depthMm: number) {
  return [
    `W#89{ ::WTs WS=${sequence}`,
    '#8015=0',
    `#1=${formatTcnNumber(xMm)}`,
    `#2=${formatTcnNumber(yMm)}`,
    `#3=-${formatTcnNumber(depthMm)}`,
    '#201=1',
    '#203=1',
    `#205=${TCN_CAM_TEMPLATE.toolId}`,
    '#1001=100',
    '#9502=0',
    '#9503=0',
    '#9505=0',
    '#9506=0',
    '#9504=0',
    '#8101=0',
    '#8096=0',
    '#8095=0',
    '#37=0',
    '#40=0',
    '#39=0',
    '#46=0',
    '#8135=0',
    '#8136=0',
    '#38=0',
    '#8180=0',
    '#8181=0',
    '#8185=0',
    '#8186=0',
    '}W',
  ].join(' ');
}

function buildRaccordOperations(
  plan: TcnMachiningPlan,
  pass: TcnToolpathPass,
  targets: readonly TcnToolpathPoint[],
) {
  return targets.map((corner, index) => {
    const guide = targets[index + 1] ?? buildTerminalGuide(plan, pass);
    return [
      'W#2129{ ::WTa',
      '#8015=0',
      '#8019=0',
      '#8017=0',
      `#8026=${formatTcnNumber(corner.xMm)}`,
      `#8027=${formatTcnNumber(corner.yMm)}`,
      `#8029=${formatTcnNumber(guide.xMm)}`,
      `#8030=${formatTcnNumber(guide.yMm)}`,
      '#42=0',
      '#49=0',
      '}W',
    ].join(' ');
  });
}

/**
 * A29/W#2129 uses the previous, target and guide points to solve a raccord.
 * The exact midpoint of an S-curve inflection can lie on the straight segment
 * between its neighbours, where the three-point construction has no solution.
 * Removing that redundant point preserves the polyline while keeping every
 * emitted raccord geometrically solvable.
 */
function removeCollinearRaccordPoints(points: readonly TcnToolpathPoint[]): TcnToolpathPoint[] {
  const simplified = [...points];
  let index = 1;

  while (index < simplified.length - 1) {
    const previous = simplified[index - 1];
    const current = simplified[index];
    const next = simplified[index + 1];
    if (!previous || !current || !next) break;

    const segmentX = next.xMm - previous.xMm;
    const segmentY = next.yMm - previous.yMm;
    const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
    if (segmentLengthSquared === 0) {
      index += 1;
      continue;
    }

    const currentX = current.xMm - previous.xMm;
    const currentY = current.yMm - previous.yMm;
    const progress = (currentX * segmentX + currentY * segmentY) / segmentLengthSquared;
    const perpendicularDistance =
      Math.abs(segmentX * currentY - segmentY * currentX) / Math.sqrt(segmentLengthSquared);

    if (progress > 0 && progress < 1 && perpendicularDistance <= RACCORD_COLLINEAR_TOLERANCE_MM) {
      simplified.splice(index, 1);
      index = Math.max(1, index - 1);
      continue;
    }

    index += 1;
  }

  return simplified;
}

function buildTerminalGuide(plan: TcnMachiningPlan, pass: TcnToolpathPass): TcnToolpathPoint {
  const last = pass.points.at(-1);
  if (!last) return { xMm: 0, yMm: plan.leadYMm };
  const exitsRight = last.xMm >= plan.stock.widthMm + TCN_CAM_TEMPLATE.openEdgeOverrunMm - 0.001;
  if (exitsRight) {
    return {
      xMm:
        pass.id === 'roughing'
          ? last.xMm
          : last.xMm - TCN_CAM_TEMPLATE.finishOpenExitGuideBacktrackMm,
      yMm: plan.leadYMm,
    };
  }
  return {
    xMm: last.xMm + TCN_CAM_TEMPLATE.terminalTangentGuideMm,
    yMm: last.yMm,
  };
}

function matchesExactReference(config: ConfiguratorState) {
  return (
    config.productId === REFERENCE_CONFIG.productId &&
    config.machineId === REFERENCE_CONFIG.machineId &&
    config.materialId === REFERENCE_CONFIG.materialId &&
    config.widthMm === REFERENCE_CONFIG.widthMm &&
    config.heightMm === REFERENCE_CONFIG.heightMm &&
    config.thicknessMm === REFERENCE_CONFIG.thicknessMm &&
    config.stockAllowanceMm.widthMm === REFERENCE_CONFIG.stockAllowanceMm.widthMm &&
    config.stockAllowanceMm.heightMm === REFERENCE_CONFIG.stockAllowanceMm.heightMm &&
    config.stockAllowanceMm.thicknessMm === REFERENCE_CONFIG.stockAllowanceMm.thicknessMm &&
    config.handle.openSide === REFERENCE_CONFIG.handle.openSide &&
    config.handle.lengthMm === REFERENCE_CONFIG.handle.lengthMm &&
    config.handle.offsetMm === REFERENCE_CONFIG.handle.offsetMm &&
    config.handle.faceWidthMm === REFERENCE_CONFIG.handle.faceWidthMm &&
    config.handle.depthMm === REFERENCE_CONFIG.handle.depthMm &&
    config.handle.transitionLengthMm === REFERENCE_CONFIG.handle.transitionLengthMm &&
    config.handle.transitionRoundness === REFERENCE_CONFIG.handle.transitionRoundness &&
    config.handle.grooveWidthMm === REFERENCE_CONFIG.handle.grooveWidthMm &&
    config.handle.roughingAllowanceMm === REFERENCE_CONFIG.handle.roughingAllowanceMm &&
    config.edgeTreatment.profile === REFERENCE_CONFIG.edgeTreatment.profile &&
    config.edgeTreatment.sizeMm === REFERENCE_CONFIG.edgeTreatment.sizeMm &&
    config.edgeTreatment.cornerRadiusMm === REFERENCE_CONFIG.edgeTreatment.cornerRadiusMm
  );
}

function collectValidationIssues(config: ConfiguratorState) {
  const issues = [...validateDoorConfig(config)];
  if (config.machineId !== REFERENCE_CONFIG.machineId) {
    issues.push({
      level: 'error',
      code: 'tcn-machine-template-unavailable',
      message:
        'TCN postprocesor je zatiaľ kalibrovaný iba pre dodaný profil stroja; pre zvolený stroj sa export nesmie spustiť.',
    });
  }
  const toolEnvelope = inspectTcnToolEnvelope(config);
  if (!toolEnvelope.fits) {
    issues.push({
      level: 'error',
      code: 'tcn-tool-envelope-does-not-fit',
      message: `Profil je príliš krátky pre kalibrovaný posun nástroja 1009 (${toolEnvelope.requiredShiftPerCapMm} mm na ukončenie; dostupné ${toolEnvelope.availableShiftPerCapMm} mm).`,
    });
  }
  const numericEntries: Array<[string, number]> = [
    ['widthMm', config.widthMm],
    ['heightMm', config.heightMm],
    ['thicknessMm', config.thicknessMm],
    ['stock-widthMm', config.stockAllowanceMm.widthMm],
    ['stock-heightMm', config.stockAllowanceMm.heightMm],
    ['stock-thicknessMm', config.stockAllowanceMm.thicknessMm],
    ['handle-lengthMm', config.handle.lengthMm],
    ['handle-offsetMm', config.handle.offsetMm],
    ['handle-faceWidthMm', config.handle.faceWidthMm],
    ['handle-depthMm', config.handle.depthMm],
    ['handle-transitionLengthMm', config.handle.transitionLengthMm],
    ['handle-transitionRoundness', config.handle.transitionRoundness],
    ['handle-grooveWidthMm', config.handle.grooveWidthMm],
    ['handle-roughingAllowanceMm', config.handle.roughingAllowanceMm],
    ['edge-sizeMm', config.edgeTreatment.sizeMm],
    ['edge-cornerRadiusMm', config.edgeTreatment.cornerRadiusMm],
  ];
  const invalidNumericFields = numericEntries
    .filter(([, value]) => !Number.isFinite(value))
    .map(([field]) => field);
  if (invalidNumericFields.length) {
    issues.push({
      level: 'error',
      code: 'tcn-non-finite-value',
      message: `TCN vstup obsahuje neplatné číselné polia: ${invalidNumericFields.join(', ')}.`,
    });
  }
  return deduplicateIssues(issues);
}

function deduplicateIssues(issues: ValidationIssue[]) {
  const seen = new Set<string>();
  return issues.filter(issue => {
    const key = `${issue.level}:${issue.code}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildWarnings(config: ConfiguratorState, releaseState: TcnReleaseState) {
  if (releaseState === 'invalid-configuration') {
    return ['Konfigurácia je neplatná; obrábacie operácie ani externé makro neboli vytvorené.'];
  }

  const shared = [
    'Pred výrobou treba overiť profil nástroja 1009 a externé makro fresatebarnesting.tmcr na cieľovom stroji.',
    'Opracovanie obvodových hrán nie je v dodanom TCN definované a tento export ho preto neobsahuje.',
  ];

  if (releaseState === 'reference-match-review-required') {
    return [
      'Výstup je bajtovo zhodný s dodaným referenčným programom; stále vyžaduje CAM simuláciu.',
      ...shared,
    ];
  }

  return [
    'TCN obsahuje dve review-only dráhy W#89/A29-W#2129 odvodené z nakonfigurovanej geometrie.',
    'Predpoklad dátumu: hotový ľavý dolný roh je X0/Y0 a prídavky polotovaru smerujú do kladných osí.',
    ...shared,
    ...(config.materialId !== REFERENCE_CONFIG.materialId
      ? ['Materiál sa zmenil; otáčky a posuvy musí potvrdiť technológ.']
      : []),
  ];
}

function buildTcnFingerprint(config: ConfiguratorState) {
  const geometry = deriveDoorGeometry(config);
  const canonicalInput = {
    schemaId: 'bdvtrade.tcn-input/v2',
    productId: config.productId,
    machineId: config.machineId,
    materialId: config.materialId,
    finishedDimensionsMm: {
      widthMm: canonicalNumber(config.widthMm),
      heightMm: canonicalNumber(config.heightMm),
      thicknessMm: canonicalNumber(config.thicknessMm),
    },
    stockAllowanceMm: {
      widthMm: canonicalNumber(config.stockAllowanceMm.widthMm),
      heightMm: canonicalNumber(config.stockAllowanceMm.heightMm),
      thicknessMm: canonicalNumber(config.stockAllowanceMm.thicknessMm),
    },
    stockDimensionsMm: {
      widthMm: canonicalNumber(geometry.stock.widthMm),
      heightMm: canonicalNumber(geometry.stock.heightMm),
      thicknessMm: canonicalNumber(geometry.stock.thicknessMm),
    },
    handle: {
      openSide: config.handle.openSide,
      lengthMm: canonicalNumber(config.handle.lengthMm),
      offsetMm: canonicalNumber(config.handle.offsetMm),
      faceWidthMm: canonicalNumber(config.handle.faceWidthMm),
      depthMm: canonicalNumber(config.handle.depthMm),
      transitionLengthMm: canonicalNumber(config.handle.transitionLengthMm),
      transitionRoundness: canonicalNumber(config.handle.transitionRoundness),
      grooveWidthMm: canonicalNumber(config.handle.grooveWidthMm),
      roughingAllowanceMm: canonicalNumber(config.handle.roughingAllowanceMm),
    },
    edgeTreatment: {
      profile: config.edgeTreatment.profile,
      sizeMm: canonicalNumber(config.edgeTreatment.sizeMm),
      cornerRadiusMm: canonicalNumber(config.edgeTreatment.cornerRadiusMm),
    },
  };
  return `tcn-${fnv1a(JSON.stringify(canonicalInput))}`;
}

function canonicalNumber(value: number): number | string {
  if (Number.isFinite(value)) return value;
  if (Number.isNaN(value)) return 'NaN';
  return value > 0 ? 'Infinity' : '-Infinity';
}

function fnv1a(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function encodeUtf16LeWithBom(text: string) {
  const bytes = new Uint8Array(2 + text.length * 2);
  bytes[0] = 0xff;
  bytes[1] = 0xfe;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    bytes[2 + index * 2] = code & 0xff;
    bytes[3 + index * 2] = code >> 8;
  }
  return bytes;
}

function formatTcnDimension(value: number) {
  return Number.isFinite(value) && value >= 0 ? formatTcnNumber(value) : '0';
}

function formatTcnNumber(value: number) {
  const rounded = Math.round(value * 1_000) / 1_000;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(',', '.');
}

function formatFileNumber(value: number) {
  return Number.isFinite(value) ? formatTcnNumber(value).replace('.', '-') : 'invalid';
}
